import React, { useEffect, useState } from 'react';
import { sanityClient } from '../../sanityClient';
import { Link } from 'react-router-dom';
import imageUrlBuilder from '@sanity/image-url';

interface Post {
  _id: string;
  title: string;
  slug: { current: string };
  excerpt?: string;
  mainImage?: { asset: { url: string } };
  publishedAt?: string;
  author?: {
    name: string;
    image?: { asset: { url: string } };
  };
}

type RecommendedBlogsVariant = 'default' | 'sidebar';

interface RecommendedBlogsProps {
  currentSlug?: string;
  variant?: RecommendedBlogsVariant;
}

const SANITY_PROJECT_ID = import.meta.env.VITE_SANITY_PROJECT_ID || "025rxk3o";
const SANITY_DATASET = import.meta.env.VITE_SANITY_DATASET || 'production';

const builder = imageUrlBuilder({
  projectId: SANITY_PROJECT_ID,
  dataset: SANITY_DATASET,
});

function getImageUrl(source: any): string {
  return builder.image(source).auto('format').url();
}

export const RecommendedBlogs: React.FC<RecommendedBlogsProps> = ({ currentSlug, variant = 'default' }) => {
  const [posts, setPosts] = useState<Post[]>([]);

  useEffect(() => {
    sanityClient.fetch(
      `*[_type == "post" && slug.current != $slug] | order(publishedAt desc)[0...3]{
        _id, 
        title, 
        slug, 
        excerpt, 
        mainImage{asset->{url}}, 
        publishedAt,
        author->{
          name,
          image{
            asset->{
              url
            }
          }
        }
      }`,
      { slug: currentSlug }
    ).then((data) => setPosts(data));
  }, [currentSlug]);

  if (!posts.length) {
    return (
      <div className={variant === 'sidebar' ? 'text-xs text-foreground/70' : 'text-center text-foreground/80'}>
        No recommendations yet.
      </div>
    );
  }

  if (variant === 'sidebar') {
    // Compact horizontal items stacked vertically for sidebar
    return (
      <div className="space-y-3">
        {posts.map(post => (
          <Link
            to={`/blog/${post.slug.current}`}
            key={post._id}
            className="flex gap-3 rounded-xl border border-border bg-card/80 hover:bg-card hover:border-primary/40 transition-all duration-200 p-2"
          >
            {post.mainImage?.asset?.url && (
              <img
                src={post.mainImage.asset.url}
                alt={post.title}
                className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                loading="lazy"
              />
            )}
            <div className="flex-1 min-w-0">
              <h3 className="text-xs font-semibold text-foreground line-clamp-2">
                {post.title}
              </h3>
              {post.publishedAt && (
                <div className="text-[10px] text-foreground/70 mt-0.5">
                  {new Date(post.publishedAt).toLocaleDateString()}
                </div>
              )}
            </div>
          </Link>
        ))}
      </div>
    );
  }

  // Default grid layout used in the main "Recommended Blogs" section
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {posts.map(post => (
        <Link 
          to={`/blog/${post.slug.current}`} 
          key={post._id} 
          className="block rounded-2xl bg-card shadow hover:shadow-xl hover:scale-[1.02] transition-all duration-300 overflow-hidden border border-border"
        >
          {post.mainImage?.asset?.url && (
            <img src={post.mainImage.asset.url} alt={post.title} className="w-full h-48 object-cover object-center" />
          )}
          <div className="p-6">
            <h3 className="text-xl font-bold text-foreground mb-2">{post.title}</h3>
            {post.excerpt && <p className="text-foreground/80 mb-3 text-sm">{post.excerpt}</p>}
            {post.author && (
              <div className="flex items-center gap-2 mb-2">
                {post.author.image?.asset?.url && (
                  <img 
                    src={getImageUrl(post.author.image)} 
                    alt={post.author.name} 
                    className="w-6 h-6 rounded-full object-cover"
                  />
                )}
                <span className="text-xs text-foreground/80 font-medium">{post.author.name}</span>
              </div>
            )}
            {post.publishedAt && <div className="text-xs text-foreground/80">{new Date(post.publishedAt).toLocaleDateString()}</div>}
          </div>
        </Link>
      ))}
    </div>
  );
};
